import { type ForwardedRef, type ReactNode } from 'react';
import clsx from 'clsx';
import { TreeSelect as PrimeTreeSelect } from 'primereact/treeselect';
import { getPlaceholder } from '../../../_utils/get-placeholder';
import { createOverflowItemTemplate } from '../../../_utils/OverflowTemplate.utils';
import { resolveOverflowPanelProps } from '../../../_utils/overlay-panel.utils';
import {
    getLabelText,
    isPlainLabelTree,
} from '../../../_utils/plain-label.utils';
import { TreeSelectSelectedPills } from './TreeSelectSelectedPills';
import { useTreeSelectSelectionModel } from '../hooks/useTreeSelectSelectionModel';
import { type TreeSelectProps } from '../models/tree-select.types';

interface TreeSelectInputProps extends TreeSelectProps {
    forwardedRef: ForwardedRef<PrimeTreeSelect>;
}

export function TreeSelectInput({
    forwardedRef,
    ...props
}: TreeSelectInputProps): ReactNode {
    const shouldUseOverflowNodeTemplate =
        !props.nodeTemplate &&
        isPlainLabelTree(
            props.options,
            (node) => getLabelText(node.label),
            (node) => node.children,
        );
    const effectiveSelectionMode = props.selectionMode ?? 'single';
    const placeholder = getPlaceholder({ ...props, isDropdown: true });
    const effectivePlaceholder =
        placeholder ||
        (effectiveSelectionMode === 'multiple' ||
        effectiveSelectionMode === 'checkbox'
            ? 'Select items'
            : 'Select item');
    const {
        display,
        handleChange,
        handleDismissSelectedValue,
        handleNodeSelect,
        handleNodeUnselect,
        selectedNodes,
        shouldRenderSelectedPills,
        value,
        valueTemplate,
    } = useTreeSelectSelectionModel({
        ...props,
        placeholder: effectivePlaceholder,
        selectionMode: effectiveSelectionMode,
    });
    const {
        checkboxSelectionBehavior: _checkboxSelectionBehavior,
        display: _display,
        onChange: _onChange,
        onNodeSelect: _onNodeSelect,
        onNodeUnselect: _onNodeUnselect,
        options,
        bulkMode: _bulkMode,
        errorClassName: _errorClassName,
        errorMessage: _errorMessage,
        focus: _focus,
        hint: _hint,
        horizontal: _horizontal,
        inputClassName: _inputClassName,
        label: _label,
        labelClassName: _labelClassName,
        labelHidden: _labelHidden,
        labelTooltip: _labelTooltip,
        placeholder: _placeholder,
        selectedValueLayout: _selectedValueLayout,
        selectionMode: _selectionMode,
        suffix: _suffix,
        suppressRequiredLabel: _suppressRequiredLabel,
        value: _value,
        valueTemplate: _valueTemplate,
        wrapperClassName: _wrapperClassName,
        validate: _validate,
        className: customClassName,
        'data-testid': dataTestId,
        ...restProps
    } = props;
    const testId = dataTestId || props.name;
    const hasInvalidState = Boolean(props.invalid || props.errorMessage);
    const effectiveDisplay = display ?? 'comma';
    const { resolvedPanelClassName } = resolveOverflowPanelProps({
        panelClassName: restProps.panelClassName,
        shouldUseOverflow: shouldUseOverflowNodeTemplate,
    });
    const nodeTemplate = createOverflowItemTemplate({
        enabled: shouldUseOverflowNodeTemplate,
        getLabel: (node) => getLabelText(node.label),
        className:
            'p-treenode-label ics-overflow-label ics-overflow-label--flex',
        fallbackTemplate: props.nodeTemplate,
    });

    return (
        <div className="ics-treeselect">
            <PrimeTreeSelect
                {...restProps}
                pt={{ root: { 'data-testid': testId } }}
                className={clsx(
                    customClassName,
                    hasInvalidState && 'p-invalid',
                )}
                ref={forwardedRef}
                display={effectiveDisplay}
                options={options}
                placeholder={effectivePlaceholder}
                selectionMode={effectiveSelectionMode}
                invalid={hasInvalidState}
                value={value}
                appendTo={document.body}
                nodeTemplate={nodeTemplate}
                panelClassName={resolvedPanelClassName}
                valueTemplate={valueTemplate}
                onNodeSelect={handleNodeSelect}
                onNodeUnselect={handleNodeUnselect}
                onChange={handleChange}
            />
            {shouldRenderSelectedPills ? (
                <TreeSelectSelectedPills
                    selectedNodes={selectedNodes}
                    onDismiss={handleDismissSelectedValue}
                    disabled={props.disabled}
                />
            ) : null}
        </div>
    );
}
